import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable, map } from 'rxjs'

import { NodeDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
// eslint-disable-next-line import/no-cycle
import {
  DocumentContextReference,
  InitialMessageRequest,
  InitialMessageResponse,
  Message,
  SendFeedbackPayload,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'


import { AiProcess } from '../services/analyst.service'

export type GenerateProcessRequest = {
  question: string
  context?: AiProcess | null
  session_id: string
  request_id: string
}

export type GenerateFromSequenceRequest = GenerateProcessRequest & {
  start_node_id: NodeDefinition['id']
  end_node_id: NodeDefinition['id']
  offset: number
}

export type GenerateProcessResponse = {
  question: string
  answer: AiProcess
  chat: string
  context: AiProcess
  session_id: string
  request_id: string
  error_message: string
}

export type GenerateProcessFromImageResponse = GenerateProcessResponse & {
  image_type: {
    image_type: string
    summary: string
  }
}

export type GenerateProcessFromDocumentResponse = { error_message: string; response: string }

@Injectable({ providedIn: 'root' })
export class AiAnalystAPI extends ApiHttpService {
  #analyst = `ai-engine/agents/analyst`

  constructor() {
    super(inject('BASE_URL' as any), inject(HttpClient))
  }

  initialMessage(payload: InitialMessageRequest): Observable<InitialMessageResponse> {
    return this.post(`${this.#analyst}/initial`, payload)
  }

  generateProcess(payload: GenerateProcessRequest): Observable<GenerateProcessResponse> {
    return this.post(`${this.#analyst}/json`, payload)
  }

  insert(payload: GenerateFromSequenceRequest): Observable<GenerateProcessResponse> {
    return this.post(`${this.#analyst}/insert`, payload)
  }

  convertBPMN(formData: FormData): Observable<GenerateProcessResponse> {
    return this.post(`${this.#analyst}/bpmn/translate`, formData)
  }

  generateProcessFromImages(formData: FormData): Observable<GenerateProcessFromImageResponse> {
    return this.post(`${this.#analyst}/image/translate`, formData)
  }

  extractProcessFromDocument(
    documentContextReference: DocumentContextReference
  ): Observable<GenerateProcessFromDocumentResponse> {
    const {
      documentId: collection_db,
      name: process_key,
      session_id: session_id,
      summary: summary,
    } = documentContextReference

    return this.post(`${this.#analyst}/process/extract`, {
      collection_db,
      process_key,
      session_id,
      summary,
    })
  }

  extractProcessFromBpmnFile(formData: FormData): Observable<GenerateProcessResponse> {
    return this.post(`${this.#analyst}/bpmn/translate`, formData)
  }

  exportToBpmn(payload: { process: AiProcess; filename?: string }): Observable<Blob> {
    return this.post(`${this.#analyst}/bpmn/export`, payload, {
      responseType: 'blob',
    })
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#analyst}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  getSSEActions(payload: InitialMessageRequest): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#analyst}/actions`, payload).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }

  getSSEGenerateProcess(formData: FormData): Observable<Omit<Message, 'author' | 'feedback'>> {
    return this.streamArray(`${this.#analyst}/chat`, null, null, formData).pipe(
      map((response: any) => {
        if (response) {
          return response
        }
        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      map((answerStr) => ({
        message: answerStr,
      }))
    )
  }
}
